/**
 * Shadow entity-link stats — frontend client.
 *
 * Mirrors the backend GET /api/emails/ai/entity-link-stats/ (Phase 4).
 * The backend writes EmailEntityLink provenance rows in SHADOW
 * (EMAIL_AI_V2_SHADOW, default on) without changing classification
 * behaviour; this hook reads the aggregated stats so the diagnostics
 * drawer can show resolution volume + how the shadow JOB resolver
 * agrees with the live Email.job_id — the evidence we use to decide
 * when to flip the behaviour-changing EMAIL_AI_V2 flag.
 *
 * Best-effort: a 404 (backend without the endpoint) or any error leaves
 * the card hidden rather than surfacing noise.
 */

import { useCallback, useEffect, useState } from 'react'
import api from '../hooks/useApi'

export interface EntityLinkJobAgreement {
  total: number
  agree: number
  live_had_no_job: number
  disagree: number
  agreement_rate: number | null
}

export interface EntityLinkStats {
  window_days: number
  since: string
  totals: { links: number; emails_with_links: number }
  by_type: Record<string, number>
  by_source: Record<string, number>
  by_status: Record<string, number>
  confidence_buckets: { high: number; medium: number; low: number; unscored: number }
  job_agreement: EntityLinkJobAgreement
  error?: string
}

interface UseEntityLinkStatsResult {
  stats: EntityLinkStats | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useEntityLinkStats(days = 30, enabled = true): UseEntityLinkStatsResult {
  const [stats, setStats] = useState<EntityLinkStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // NOTE: the shared axios client already carries baseURL `/api`;
      // a leading `/api/` here produced the prod `/api/api/...` 404s.
      const { data } = await api.get<EntityLinkStats>(
        `/emails/ai/entity-link-stats/?days=${days}`,
      )
      setStats(data ?? null)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      // 404 = backend without the endpoint; treat as "no data" (hidden card).
      if (status === 404) {
        setStats(null)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load stats')
      }
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    if (enabled) void refresh()
  }, [enabled, refresh])

  return { stats, loading, error, refresh }
}
